/**
 * Multipart attachment upload for the generated SDK (AMP-505).
 *
 * The generated `attachmentsCreate` endpoint can't carry a file: orval picks
 * the first request content type in the schema (`application/json`) and types
 * the `file` field as a string, so it `JSON.stringify`s the body. The
 * `/api/v1/attachments/` endpoint also accepts `multipart/form-data`, which is
 * what an actual file upload needs.
 *
 * This thin helper posts that multipart body through the SDK mutator, so it
 * shares the same auth / host / error-wrapping / FormData handling as every
 * generated call (`customFetch` skips the JSON content-type when the body is a
 * `FormData`). It lives outside `generated/` for the same reason
 * `liftApiMutator` does — it's the hand-written boundary the generator can't
 * express. The response is parsed against the hand-written Attachment schema so
 * the domain type flows out without a cast and drift surfaces as an error.
 */
import type { Attachment as GeneratedAttachment, FileTypeEnum } from '~/data/generated/model';
import { customFetch } from '~/data/liftApiMutator';
import { parseApiResponse } from '~/data/parseApiResponse';
import { type Attachment, attachmentSchema } from '~/data/schemas/attachment';

export interface UploadAttachmentParams {
  file: File;
  name: string;
  fileType: FileTypeEnum;
}

export async function uploadAttachment(
  { file, name, fileType }: UploadAttachmentParams,
  // Only the cancellation signal is forwarded to the underlying fetch — narrowed
  // from the full RequestInit so a caller can't override the fixed POST /
  // multipart-body semantics (e.g. a stray `headers` clobbering the FormData
  // boundary).
  options?: Pick<RequestInit, 'signal'>,
): Promise<Attachment> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  // The API field is snake_case; multipart form keys bypass the camelCase
  // middleware, so send the underscored name the serializer expects.
  formData.append('file_type', fileType);

  const { data } = await customFetch<{
    data: GeneratedAttachment;
    status: number;
    headers: Headers;
  }>('/api/v1/attachments/', {
    ...options,
    method: 'POST',
    body: formData,
    redirect: 'follow',
  });

  return parseApiResponse(attachmentSchema, data, 'Attachment');
}
